import { FiltersActions } from 'shared/common/OpenFiltersButton/model/slice'
import { useAppSearchParams } from 'shared/libs/hooks/useAppSearchParams'
import { Filter } from 'shared/types/Common'
import { AllPageParams, ParamKey } from 'shared/types/QueryModel'

import { useEffect, useState } from 'react'

import {
  useAppDispatch,
  useAppSelector,
} from 'shared/config/redux/hooks/useRedux'

interface FilterValue {
  key: ParamKey
  value: string
}
type Params = ParamKey[]

export const useFilter = (filterParams?: Params) => {
  const dis = useAppDispatch()
  const { setParams, getParam } = useAppSearchParams()

  const { isOpen, isUseFilters, filters } =
    useAppSelector((state) => state.filters) || {}
  const [filtersCount, setFiltersCount] = useState<number>(0)

  const setIsOpen = (value: boolean) => {
    dis(FiltersActions.setIsOpen(value))
  }

  const setIsUseFilters = (value: boolean) => {
    dis(FiltersActions.setIsUseFilters(value))
  }

  const setFilters = (filters: Filter[]) => {
    filters.forEach((filter) => {
      dis(FiltersActions.setFilters(filter))
    })
  }

  const clearFilters = () => {
    dis(FiltersActions.clearFilters())
  }

  const deleteFilter = (param: ParamKey) => {
    dis(FiltersActions.deleteFilter(param))
  }

  const setFiltersToParams = () => {
    if (filters) {
      const preparedFilters = Object.entries(filters).map(([key, value]) => ({
        key,
        value,
      })) as FilterValue[]
      const isHasPageParam = getParam('page')
      setParams([
        ...preparedFilters,
        ...(isHasPageParam ? [{ key: 'page' as ParamKey, value: '1' }] : []),
      ])
    }
  }

  const countFiltersCount = (filters: Filter) => {
    if (filterParams && filters) {
      const allFilterParams = filterParams
        .map((filterParam) => filters[filterParam as keyof AllPageParams])
        .filter(Boolean) as string[]

      const calculatedFiltersCount = allFilterParams
        .map((param) => param.split(','))
        .flat().length

      return calculatedFiltersCount
    }
  }

  const countFilters = (filters: ParamKey[]): Filter[] => {
    return filters
      ?.map((filter) => {
        const filterFound = getParam(filter)
        if (filterFound) return { [filter]: filterFound }
        return null
      })
      .filter(Boolean) as Filter[]
  }

  const checkIsRelevantFilters = (filterParams: Params) => {
    const countedFilters = countFilters(filterParams)
    return !countedFilters?.some((countedFilter) => {
      const filterKey = Object.keys(countedFilter)[0]
      const filterValue = Object.values(countedFilter)[0]
      return filters?.[filterKey as keyof AllPageParams] !== filterValue
    })
  }

  useEffect(() => {
    const filtersCount = (filters ? countFiltersCount(filters) : 0) as number
    setFiltersCount(filtersCount)
  }, [filters])

  return {
    filtersCount,
    setIsOpen,
    setIsUseFilters,
    isOpen,
    isUseFilters,
    setFilters,
    filters,
    clearFilters,
    setFiltersToParams,
    deleteFilter,
    countFilters,
    checkIsRelevantFilters,
  }
}
